import getUTCNormalizedTimestamp from 'ui-forms-embed-components-library/utils/getUTCNormalizedTimestamp';
import { CONTACT_EMAIL_PROPERTY_REFERENCE, RECAPTCHA_PROPERTY_REFERENCE } from '../constants/common';
import { EMAIL_CONSENT_MODULE_TYPE, LEGAL_BASIS_FOR_PROCESSING_MODULE_TYPE } from '../constants/moduleTypes';
import { MODULE_DEFINITION_ELEMENT_TYPE_VALUES } from '../types/module';
import isEmailLike from './isEmailLike';
import isISODate from './isISODate';
import { moduleDataPrivacyResolvePropertyModules } from './moduleDataPrivacy';
import { moduleCommunicationConsentResolvePropertyModules } from './moduleCommunicationConsent';
import { moduleLegalBasisForProcessingResolvePropertyModules } from './moduleLegalBasisForProcessing';
export const moduleIsParent = module => !!module.modules;
export const moduleIsDataPrivacy = module => module.type === 'dataPrivacy';
export const moduleIsCommunicationConsent = module => module.type === EMAIL_CONSENT_MODULE_TYPE;
export const moduleIsLegalBasisForProcessing = module => module.type === LEGAL_BASIS_FOR_PROCESSING_MODULE_TYPE;
export const moduleIsReCaptcha2 = module => module.type === 'reCaptcha2';
export const moduleIsReCaptcha3 = module => module.type === 'reCaptcha3';
export const moduleIsHiddenType = module => module.type === 'hidden';
export const moduleIsProperty = module => !!module.propertyReference;
export const moduleIsEmailContactProperty = module => moduleIsProperty(module) && module.propertyReference === CONTACT_EMAIL_PROPERTY_REFERENCE;
export const moduleIsElement = module => MODULE_DEFINITION_ELEMENT_TYPE_VALUES.includes(module.type);
export const moduleGetPropertyName = propertyReference => propertyReference.includes('/') ? propertyReference.split('/')[1] : propertyReference;
export const moduleReCaptcha2ToProperty = module => ({
  id: module.id,
  type: 'reCaptcha2',
  propertyReference: RECAPTCHA_PROPERTY_REFERENCE,
  label: 'ReCaptcha2',
  required: true,
  conditionallyHidden: false,
  triggerDependentRulesOnLoad: false
});
export const moduleReCaptcha3ToProperty = module => ({
  id: module.id,
  type: 'reCaptcha3',
  propertyReference: RECAPTCHA_PROPERTY_REFERENCE,
  label: 'ReCaptcha3',
  required: true,
  conditionallyHidden: false,
  triggerDependentRulesOnLoad: false
});
export const moduleResolveLogicModules = (acc, module) => {
  if (moduleIsParent(module)) {
    return module.modules.reduce(moduleResolveLogicModules, acc);
  }
  if (moduleIsDataPrivacy(module)) {
    acc.push(...moduleDataPrivacyResolvePropertyModules(module));
  }
  if (moduleIsCommunicationConsent(module)) {
    acc.push(...moduleCommunicationConsentResolvePropertyModules(module));
  }
  if (moduleIsReCaptcha2(module)) {
    acc.push(moduleReCaptcha2ToProperty(module));
  }
  if (moduleIsReCaptcha3(module)) {
    acc.push(moduleReCaptcha3ToProperty(module));
  }
  if (moduleIsLegalBasisForProcessing(module)) {
    acc.push(...moduleLegalBasisForProcessingResolvePropertyModules(module));
  }
  if (moduleIsProperty(module)) {
    acc.push(module);
  }
  if (moduleIsElement(module)) {
    acc.push(module);
  }
  return acc;
};
export const moduleDeserializeValue = (module, value) => {
  switch (module.type) {
    case 'date':
      {
        if (isISODate(value[0])) {
          const [year, month, day] = value[0].split('-').map(Number);
          return getUTCNormalizedTimestamp({
            year,
            month,
            day
          });
        } else if (/^\d+$/.test(value[0])) {
          return value[0];
        }
        return '';
      }
    case 'singleCheckbox':
      return value[0] === 'true';
    case 'multipleCheckbox':
      {
        var _module$options;
        return (_module$options = module.options) === null || _module$options === void 0 ? void 0 : _module$options.filter(opt => value.some(v => v.toLowerCase() === opt.value.toLowerCase() || v.toLowerCase() === opt.label.toLowerCase())).map(opt => opt.value);
      }
    case 'dropdownSelect':
    case 'radioSelect':
      {
        var _module$options2;
        return (_module$options2 = module.options) === null || _module$options2 === void 0 || (_module$options2 = _module$options2.find(opt => value.some(v => v.toLowerCase() === opt.value.toLowerCase() || v.toLowerCase() === opt.label.toLowerCase()))) === null || _module$options2 === void 0 ? void 0 : _module$options2.value;
      }
    case 'email':
      return isEmailLike(value[0]) ? value[0].trim().replace(/ /g, '+') : value[0];
    case 'phoneNumber':
      return !value[0].trim() || value[0].trim().startsWith('+') ? value[0].trim() : `+${value[0].trim()}`;
    case 'hidden':
      {
        return value.map(v => isEmailLike(v) ? v.trim().replace(/ /g, '+') : v);
      }
    default:
      return value[0];
  }
};
export const moduleResolveDefaultValue = (module, queryParams) => {
  var _module$options$find$, _module$options4, _ref, _module$checked;
  // Legal basis value should never be overwritten by query params
  if (module.type === LEGAL_BASIS_FOR_PROCESSING_MODULE_TYPE) {
    var _module$options3;
    return {
      value: ((_module$options3 = module.options) === null || _module$options3 === void 0 || (_module$options3 = _module$options3[0]) === null || _module$options3 === void 0 ? void 0 : _module$options3.value) || '',
      isFromQueryParams: false
    };
  }

  // if property was passed as query parameter
  const maybeQueryParamValue = queryParams[module.propertyReference] || queryParams[module.propertyReference.split('/')[1]];
  if (maybeQueryParamValue) {
    const queryValue = maybeQueryParamValue.includes(';') ? maybeQueryParamValue.split(';') : maybeQueryParamValue;
    const queryArrayValue = Array.isArray(queryValue) ? queryValue : [queryValue];
    return {
      value: moduleDeserializeValue(module, queryArrayValue),
      isFromQueryParams: true
    };
  }
  switch (module.type) {
    case 'radioSelect':
    case 'dropdownSelect':
      return {
        value: (_module$options$find$ = (_module$options4 = module.options) === null || _module$options4 === void 0 || (_module$options4 = _module$options4.find(opt => opt.selected)) === null || _module$options4 === void 0 ? void 0 : _module$options4.value) !== null && _module$options$find$ !== void 0 ? _module$options$find$ : undefined,
        isFromQueryParams: false
      };
    case 'hidden':
      {
        return {
          value: module.values,
          isFromQueryParams: false
        };
      }
    default:
      return {
        value: (_ref = (_module$checked = module.checked) !== null && _module$checked !== void 0 ? _module$checked : module.defaultValue) !== null && _ref !== void 0 ? _ref : module.options ? module.options.filter(opt => opt.selected).map(opt => opt.value) : undefined,
        isFromQueryParams: false
      };
  }
};
export const moduleIsModuleOrChildOf = (moduleId, module) => {
  // early cut off, because module we are looking for might be a parent module
  if (module.id === moduleId) return true;
  if (moduleIsParent(module)) {
    return module.modules.some(subModule => moduleIsModuleOrChildOf(moduleId, subModule));
  }
  return false;
};
export const modulesFindByType = (modules, type) => {
  for (const module of modules) {
    if (module.type === type) {
      return module;
    }
    if (moduleIsParent(module)) {
      const found = modulesFindByType(module.modules, type);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
};
export const moduleFindById = (modules, id) => {
  for (const module of modules) {
    if (module.id === id) {
      return module;
    }
    if (moduleIsParent(module)) {
      const found = moduleFindById(module.modules, id);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
};
export const moduleIsAllDirectChildrenHidden = (module, modules, shorteningCompleted) => moduleIsParent(module) && module.modules.every(child => modules[child.id] && modules[child.id].hidden || moduleIsHiddenType(child) || moduleIsDataPrivacy(child) && !shorteningCompleted);